import { useEffect, useRef, useState, type ChangeEvent } from "react";
import { copy } from "../copy";
import Icon from "../components/Icon";
import { api, uploadToS3, type Me } from "./api";

const MAX_BYTES = 8 * 1024 * 1024;
const TYPES = ["image/png", "image/jpeg", "image/webp"];

function objectURL(file: File): string {
  return typeof URL.createObjectURL === "function" ? URL.createObjectURL(file) : "";
}

function revoke(url: string) {
  if (url && typeof URL.revokeObjectURL === "function") URL.revokeObjectURL(url);
}

export default function PhotoTab({ me, onSaved }: { me: Me; onSaved: (me: Me) => void }) {
  const t = copy.dashboard.photo;
  const [file, setFile] = useState<File | null>(null);
  const [draftURL, setDraftURL] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: "ok" | "error"; text: string } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => () => revoke(draftURL), [draftURL]);

  const pick = (event: ChangeEvent<HTMLInputElement>) => {
    const next = event.target.files?.[0];
    event.target.value = "";
    if (!next) return;
    if (!TYPES.includes(next.type)) {
      setMessage({ tone: "error", text: t.badType });
      return;
    }
    if (next.size <= 0 || next.size > MAX_BYTES) {
      setMessage({ tone: "error", text: t.tooBig });
      return;
    }
    setFile(next);
    setDraftURL(objectURL(next));
    setMessage(null);
  };

  const save = async () => {
    if (!file) return;
    setBusy(true);
    setMessage(null);
    const presigned = await api.presignAvatar(file.type, file.size);
    const uploaded = presigned.ok && (await uploadToS3(presigned.data.upload_url, file, presigned.data.headers));
    const confirmed = presigned.ok && uploaded ? await api.confirmAvatar(presigned.data.key) : null;
    setBusy(false);
    if (confirmed?.ok) {
      onSaved(confirmed.data);
      setFile(null);
      setDraftURL("");
      setMessage({ tone: "ok", text: t.saved });
      return;
    }
    setMessage({ tone: "error", text: !presigned.ok ? presigned.error : confirmed && !confirmed.ok ? confirmed.error : t.failed });
  };

  const remove = async () => {
    setBusy(true);
    setMessage(null);
    const res = await api.deleteAvatar();
    setBusy(false);
    if (res.ok) {
      onSaved(res.data);
      setFile(null);
      setDraftURL("");
      setMessage({ tone: "ok", text: t.removed });
      return;
    }
    setMessage({ tone: "error", text: res.error });
  };

  const preview = draftURL || me.logo_url;

  return (
    <div className="rounded-2xl border border-ground-edge bg-ground-raised p-5 md:p-7">
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[260px_1fr] lg:gap-10">
        <div className="relative aspect-square w-full overflow-hidden rounded-2xl border border-ground-edge bg-ground">
          {preview ? (
            <img src={preview} alt={t.alt(me.username)} className="h-full w-full object-cover object-top" />
          ) : (
            <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-chrome-dim">
              <Icon name="user3" className="text-4xl" />
              <span className="text-[11px] uppercase tracking-[0.16em]">{t.none}</span>
            </div>
          )}
        </div>

        <div>
          <h3 className="font-headline text-2xl uppercase leading-none text-chrome">{t.heading}</h3>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-chrome-dim">{t.sub}</p>

          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="bf-focusable mt-6 flex w-full cursor-pointer flex-col items-center justify-center rounded-2xl border border-dashed border-ground-edge bg-ground px-6 py-8 text-center transition-colors hover:border-gold/50"
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-ground-raised text-xl text-gold">
              <Icon name="uploadCloud" />
            </span>
            <span className="mt-3 text-sm text-chrome">{file ? file.name : t.pick}</span>
            <span className="mt-1 text-[11px] uppercase tracking-[0.14em] text-chrome-dim">{t.rules}</span>
          </button>
          <input
            ref={inputRef}
            type="file"
            accept={TYPES.join(",")}
            onChange={pick}
            aria-label={t.inputLabel}
            className="hidden"
          />

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={save}
              disabled={!file || busy}
              className="bf-focusable h-11 cursor-pointer whitespace-nowrap rounded-md bg-money px-6 text-[11px] font-bold uppercase tracking-[0.14em] text-ground transition-colors hover:bg-money-deep disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy && file ? t.saving : t.save}
            </button>
            {me.logo_url && (
              <button
                type="button"
                onClick={remove}
                disabled={busy}
                className="bf-focusable h-11 cursor-pointer whitespace-nowrap rounded-md border border-ground-edge px-6 text-[11px] font-bold uppercase tracking-[0.14em] text-chrome-dim transition-colors hover:border-alert/50 hover:text-alert disabled:opacity-50"
              >
                {t.remove}
              </button>
            )}
          </div>

          {message && (
            <p
              role={message.tone === "error" ? "alert" : "status"}
              className={`bf-fade-in mt-4 text-[11px] font-bold uppercase tracking-[0.14em] ${message.tone === "error" ? "text-alert" : "text-money"}`}
            >
              {message.text}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
